import { BedIcon, CalendarBlankIcon, UserIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { type ReactNode } from "react";
import { useIntl } from "react-intl";

import { Divider } from "@/components/Divider/Divider";
import { LazyImage } from "@/components/Image/LazyImage";
import { BoardBasisName } from "@/features/hotels/components/hotel/BoardBasisName";
import { boardIcons } from "@/features/hotels/components/hotels/BoardIcons";
import { HotelName } from "@/features/hotels/components/hotels/HotelName";
import { HotelStars } from "@/features/hotels/components/hotels/HotelStars";
import type { FindUserReservationsExternalHotelReservation } from "@/types/codegen";

export const BookedHotelCard = ({
  onClick,
  reservation,
  footer,
}: {
  reservation: FindUserReservationsExternalHotelReservation;
  onClick?: () => void;
  footer?: ReactNode;
}) => {
  const intl = useIntl();

  // show hotel check-in and check-out dates
  const startDate = reservation.serviceDates.startDate.split("T")[0];
  const endDate = reservation.serviceDates.endDate.split("T")[0];
  const { hotel } = reservation;

  const children = reservation.travelers.filter(
    (traveler) => traveler.type === "child",
  );

  const adults = reservation.travelers.filter(
    (traveler) => traveler.type === "adult",
  );

  const boardBasis = Array.from(
    new Set(reservation.rooms.map(({ boardBasis }) => boardBasis).flat()),
  );

  return (
    <button
      type="button"
      className="section-card w-full text-start"
      onClick={onClick}
    >
      <div className="flex w-full justify-between gap-4">
        <div className="flex min-h-[130px] w-[90px] shrink-0 items-center overflow-clip rounded-2xl bg-gray-100 text-center text-xs text-text-tertiary">
          {hotel.mainImageUrl ? (
            <LazyImage
              className="h-full w-full object-cover"
              sentryElementClassName="self-start"
              src={hotel.mainImageUrl}
              alt={hotel.name}
            />
          ) : (
            <span className="grow">
              {intl.formatMessage({
                defaultMessage: "No image available",
                description:
                  "Fallback alt text shown when no hotel or room image is available",
              })}
            </span>
          )}
        </div>

        <div className="flex w-full flex-col">
          <span className="mb-1 line-clamp-2 text-base/snug font-semibold text-text-title">
            <HotelName name={hotel.name} />
          </span>

          {!!hotel.stars && <HotelStars stars={hotel.stars} />}

          <ul className="mt-2 flex flex-col gap-2">
            {boardBasis.map((bb) => (
              <li key={bb} className="flex items-center">
                <div className="flex items-center gap-2 text-xs">
                  {boardIcons[bb]}
                  <BoardBasisName boardBasis={bb} />
                </div>
              </li>
            ))}

            <li className="flex items-center justify-start gap-2 text-xs">
              <BedIcon size={16} />
              <span>
                {intl.formatMessage(
                  {
                    defaultMessage: "{count}x Rooms",
                    description: "Room count in a hotel booking summary",
                  },
                  { count: reservation.rooms.length },
                )}
              </span>
            </li>

            <li className="flex items-center justify-start gap-2 text-xs">
              <CalendarBlankIcon size={16} />
              <span>
                {dayjs(startDate).format("ddd, DD MMMM")} -{" "}
                {dayjs(endDate).format("ddd, DD MMMM")}
              </span>
            </li>

            <li className="flex items-center justify-start gap-2 text-xs">
              <UserIcon size={16} />
              <span>
                {children.length > 0 ? (
                  <>
                    {intl.formatMessage(
                      {
                        defaultMessage:
                          "{count, plural, one {# child} other {# children}}",
                        description: "Children count in a booking summary",
                      },
                      { count: children.length },
                    )}{" "}
                  </>
                ) : null}
                {intl.formatMessage(
                  {
                    defaultMessage:
                      "{count, plural, one {# adult} other {# adults}}",
                    description: "Adult count in a booking summary",
                  },
                  { count: adults.length },
                )}
              </span>
            </li>
          </ul>
        </div>
      </div>

      {footer ? (
        <>
          <div className="my-4">
            <Divider color="neutral-softer" />
          </div>

          {footer}
        </>
      ) : null}
    </button>
  );
};
